import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  DataTable,
  FieldSelect,
  LoadingState,
  PageHeader,
  SectionHeader,
} from '../../../group_leader/components/common'
import PlayerRow from '../common/PlayerRow'
import { makeT } from '../../../group_leader/helpers/i18n'
import { buildSlots, playerFullName } from '../../../group_leader/helpers/player'
import { playsWomensTee, sheetGenderTees, teeGroupsFor } from '../../helpers/players'
import {
  HOLES_PLAYED_KEYS, holeTypeFromTee, holesTypeFor, NINE_HOLES, teeWritesForField, walkonHoleTypeForSave, walkonStartingHole,
} from '../../helpers/pairings'
import { placeWalkonField } from '../../helpers/walkonPlacement'
import {
  glApi,
  useListLeagueCoursesQuery,
  useListRoundsQuery,
  useGetTeeSheetQuery,
  useGetUnplacedPlayersQuery,
  useCreateRowMutation,
  useSetCourseAndTeeMutation,
  useSetHoleMutation,
  useUpdateRoundExpressEventSettingsMutation,
} from '../../../group_leader/store/glApi'
import { showErrorNotification } from '../../../shared/helpers'
import LiveMessage from '../common/LiveMessage'
import WizardFooter from '../common/WizardFooter'
import { goToNextStep, goToPreviousStep, markStepCompleted, setSetupConfig } from '../../store/wizardSlice'

const t = makeT('group_leader.event_setup.course_tee')

// A course's default tee for a gender ('M'/'F', '' for none): its own, else the ungendered one, else its first.
const defaultTeeFor = (course, gender) => {
  const byGender = course?.attributes?.default_tee_by_gender || {}
  return String(byGender[gender] || byGender[''] || course?.attributes?.tees?.[0]?.id || '')
}

// One row per foursome, one tee select per golfer; the event's tee and Apply live in the card above.
const IndividualTeeSection = ({
  foursomes,
  seatCount,
  tees,
  defaultTeeId,
  defaultWomensTeeId,
  onPlayerTeeChange,
  loadingTeePlayerIds,
  preAssigning,
}) => {
  const populatedGroups = foursomes.filter((g) => (g.attributes?.players || []).length > 0)
  const placedCount = populatedGroups.reduce((sum, g) => sum + g.attributes.players.length, 0)

  // Front/Back 9 puts a golfer on the tee's virtual twin, so a miss falls back to its parent.
  const courseTeeIds = new Set(tees.map((tee) => String(tee.id)))
  const savedTeeId = (player) => {
    const currentTeeId = player.tee?.id ? String(player.tee.id) : ''
    if (!currentTeeId) { return '' }
    if (courseTeeIds.has(currentTeeId)) { return currentTeeId }
    const parentId = player.tee?.parent_id ? String(player.tee.parent_id) : ''
    return courseTeeIds.has(parentId) ? parentId : ''
  }

  // Nothing saved on this course: show the card's tee, else the course's first; the badge reports it unsaved.
  const cardTeeFor = (player) => {
    const fallback = playsWomensTee(player) ? (defaultWomensTeeId || defaultTeeId) : defaultTeeId
    if (courseTeeIds.has(String(fallback))) { return String(fallback) }
    return tees.length > 0 ? String(tees[0].id) : ''
  }

  const teeSelect = (player, className) => {
    const pid = player.player_round_id
    const currentTeeId = savedTeeId(player) || cardTeeFor(player)
    return (
      <select
        className={`gl-event-setup-tee-sel ${className}`}
        value={currentTeeId}
        onChange={(e) => onPlayerTeeChange(pid, e.target.value)}
        disabled={loadingTeePlayerIds.includes(pid) || preAssigning}
        aria-label={`${t('individual_player_tee_aria_prefix')} ${playerFullName(player)}`}
        >
        {tees.map((tee) => (
          <option key={tee.id} value={tee.id}>{tee.name}</option>
        ))}
      </select>
    )
  }

  // Measured against the event's tee, so the badge means "off the event default".
  const isOffDefaultTee = (player) => {
    const currentTeeId = savedTeeId(player)
    return !!defaultTeeId && currentTeeId !== String(defaultTeeId)
  }

  // No tee at all, or a tee other than the event's; role="img" so the label is announced.
  const exceptionBadge = (player) => {
    const missing = !savedTeeId(player)
    if (!missing && !isOffDefaultTee(player)) { return null }
    const label = t(missing ? 'missing_tee_badge_aria' : 'exception_badge_aria')
    return (
      <span className={`gl-event-setup-pit-badge${missing ? ' is-missing' : ''}`} role="img" aria-label={label} title={label}/>
    )
  }

  return (
    <>
      <SectionHeader>{t('individual_tees')}</SectionHeader>

      {placedCount === 0 && <div className="gl-event-setup-sheet-msg">{t('no_golfers')}</div>}

      {placedCount > 0 && (
        <div className="gl-event-setup-sheet-wrap">
          <div className="gl-event-setup-ct-readonly-banner" role="note">
            <span className="gl-event-setup-ct-readonly-icon" aria-hidden="true">ⓘ</span>
            <span>{t('sheet_readonly_note')}</span>
          </div>
          <DataTable
            columns={[
              { key: 'hole', label: t('col_hole'), className: 'gl-event-setup-ct-col-hole' },
              { key: 'time', label: t('col_tee_time'), className: 'gl-event-setup-ct-col-time' },
              ...Array.from({ length: seatCount }, (_, i) => ({
                key: `slot-${i}`,
                label: `${t('col_golfer')} ${i + 1}`,
                slotIndex: i,
                className: 'gl-event-setup-ct-col-slot',
              })),
            ]}
            rows={populatedGroups}
            rowKey={(group) => group.id}
            ariaLabel={t('sheet_aria_label')}
            renderCell={(group, column) => {
              const attrs = group.attributes
              if (column.key === 'hole') {
                return <div className="gl-event-setup-ct-sh-hole">{attrs.hole || attrs.tee_number || 1}</div>
              }
              if (column.key === 'time') {
                return <div className="gl-event-setup-ct-sh-time">{attrs.tee_time || '-'}</div>
              }
              // By seat, not array index, so the "Golfer N" headings match the tee sheet's.
              const player = buildSlots(attrs.players || [], attrs.blinds || [], seatCount)[column.slotIndex]
              if (!player || player._type === 'blind') { return null }
              return (
                <div
                  className={`gl-event-setup-ct-sh-slot${isOffDefaultTee(player) ? ' gl-event-setup-ct-sh-slot--ex' : ''}`}
                >
                  <div className="gl-event-setup-ct-sh-top">
                    <PlayerRow player={player}/>
                    {exceptionBadge(player)}
                  </div>
                  {teeSelect(player, 'gl-event-setup-ct-sh-sel')}
                </div>
              )
            }}
            renderMobileRow={(group) => (
              <div className="gl-event-setup-ct-mc">
                <div className="gl-event-setup-ct-mc-hd">
                  <span>{t('col_hole')} {group.attributes.hole || group.attributes.tee_number || 1}</span>
                  {group.attributes.tee_time && <span> · {group.attributes.tee_time}</span>}
                </div>
                {group.attributes.players.map((player) => (
                  <div
                    key={player.player_round_id}
                    className={`gl-event-setup-ct-mc-slot${isOffDefaultTee(player) ? ' gl-event-setup-ct-sh-slot--ex' : ''}`}
                  >
                    <PlayerRow player={player}/>
                    {exceptionBadge(player)}
                    {teeSelect(player, 'gl-event-setup-ct-mc-tee-sel')}
                  </div>
                ))}
              </div>
            )}
          />
        </div>
      )}
    </>
  )
}

IndividualTeeSection.propTypes = {
  foursomes: PropTypes.array.isRequired,
  // Seats per group, from the round, so a fivesome or sixsome shows every golfer.
  seatCount: PropTypes.number.isRequired,
  tees: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })).isRequired,
  // The event's tee, which the exception badge is measured against.
  defaultTeeId: PropTypes.string,
  defaultWomensTeeId: PropTypes.string,
  onPlayerTeeChange: PropTypes.func.isRequired,
  loadingTeePlayerIds: PropTypes.array.isRequired,
  preAssigning: PropTypes.bool,
}

const CourseTeeStep = () => {
  const dispatch = useDispatch()
  const leagueId = useSelector((s) => s.misc.data.leagueId)
  const roundId = useSelector((s) => s.wizard.roundId)
  const setupConfig = useSelector((s) => s.wizard.setupConfig)

  const { data: coursesData, isLoading: coursesLoading } = useListLeagueCoursesQuery(
    leagueId, { skip: !leagueId },
  )
  const courses = coursesData?.data || []

  const { data: roundsData } = useListRoundsQuery(leagueId, { skip: !leagueId })
  const serverRound = roundsData?.data?.find((r) => String(r.id) === String(roundId))
  // Walk-on (group size 1) has no per-player tees, so this step collects the event's course, tees and holes.
  const isWalkon = setupConfig?.startType === 'walkon'
    || String(serverRound?.attributes?.pairing_group_size) === '1'

  const { data: teeSheetData, isLoading: teeSheetLoading, refetch: refetchTeeSheet } = useGetTeeSheetQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId },
  )

  const { data: unplacedData } = useGetUnplacedPlayersQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId },
  )

  const [ setCourseAndTee, { isLoading: saving } ] = useSetCourseAndTeeMutation()
  const [ createRow ] = useCreateRowMutation()
  const [ setHole ] = useSetHoleMutation()
  const [ placingWalkon, setPlacingWalkon ] = useState(false)
  const [ liveMessage, setLiveMessage ] = useState('')
  // Toasted and announced: react-notifications carries no aria-live of its own.
  const notifyFailure = (message) => { showErrorNotification(message); setLiveMessage(message) }
  const [ updateRoundExpressEventSettings, { isLoading: savingWalkon } ] = useUpdateRoundExpressEventSettingsMutation()

  // Walk-on defaults: the men's and women's tee, and the holes played (1=18, 2=front 9, 3=back 9).
  const [ teeM, setTeeM ] = useState(setupConfig?.walkonTeeM || '')
  const [ teeF, setTeeF ] = useState(setupConfig?.walkonTeeF || '')
  const [ holeType, setHoleType ] = useState(setupConfig?.walkonHoleType || '1')

  const [ mode, setMode ] = useState('same')
  const [ courseId, setCourseId ] = useState(setupConfig?.courseId || '')
  const [ applyingToAll, setApplyingToAll ] = useState(false)
  const [ loadingTeePlayerIds, setLoadingTeePlayerIds ] = useState([])
  const [ preAssigning, setPreAssigning ] = useState(false)

  const serverInitDone = useRef(false)

  // One call per distinct tee, so every golfer holds an explicit tee_id; tee-less golfers take fallbackTeeId.
  const preAssignAllPlayerTees = async (currentFoursomes, fallbackTeeId = '') => {
    const entries = teeWritesForField(currentFoursomes.flatMap((f) => f.attributes?.players || []), { fallbackTeeId })
    if (!entries.length || !roundId) { return }
    setPreAssigning(true)
    try {
      for (const [ assignTeeId, pids ] of entries) {
        // eslint-disable-next-line no-await-in-loop
        const result = await setCourseAndTee({
          leagueId, roundId, teeId: assignTeeId, playerIds: pids, silent: true,
        })
        // Checked: this never runs again, and a golfer it misses keeps no tee for score entry.
        if (result.error) { notifyFailure(t('errors.tee_assignment_failed')); return }
      }
    } finally {
      dispatch(glApi.util.invalidateTags([ 'TeeSheet' ]))
      setPreAssigning(false)
    }
  }

  // Saved as the step loads, not only on Continue, so golfers never end up with no tee; each gender keeps its own.
  const assignDefaultTeeToPlayers = async ({ men, women }, currentFoursomes) => {
    if (!men || !roundId) { return }
    const players = currentFoursomes.flatMap((f) => f.attributes?.players || [])
    const { groups } = teeGroupsFor(players, { menTee: String(men), womenTee: String(women || men) })
    setPreAssigning(true)
    try {
      for (const [ teeId, playerIds ] of groups) {
        if (!playerIds.length) { continue }
        // eslint-disable-next-line no-await-in-loop
        const result = await setCourseAndTee({ leagueId, roundId, teeId, playerIds: playerIds.map(String) })
        if (result.error) { showErrorNotification(t('errors.tee_assignment_failed')); return }
      }
    } finally {
      setPreAssigning(false)
    }
  }

  useEffect(() => {
    if (serverInitDone.current || !courses.length) { return }
    if (teeSheetLoading) { return }
    serverInitDone.current = true

    const teeSheetFoursomes = teeSheetData?.data || []
    const sheetPlayers = teeSheetFoursomes.flatMap((f) => f.attributes?.players || [])

    // The golfers' tee, else the one recorded before walk-on, else the first course's default.
    const firstPlayerTee = sheetPlayers.find((p) => p?.tee?.id)?.tee
    const fallbackCourse = courses[0]
    const defaultTeeId = (firstPlayerTee && String(firstPlayerTee.id))
      || (setupConfig?.teeId && String(setupConfig.teeId))
      || defaultTeeFor(fallbackCourse, 'M')

    // What the field plays is the truth, as the redux setup is gone after a reload. Individual
    // means golfers of one gender on different tees: "same tee for all" writes one per gender.
    const sheetTees = sheetGenderTees(sheetPlayers)
    const isIndividual = !sheetTees
    if (isIndividual) { setMode('individual') }
    // The selects list the course's own tees, so a Front/Back 9 tee shows as its parent.
    const selectId = (tee) => String(tee.parent_id || tee.id)
    if (sheetTees?.men) { setTeeM(selectId(sheetTees.men)) }
    if (sheetTees?.women) { setTeeF(selectId(sheetTees.women)) }

    if (!setupConfig?.courseId) {
      const observedCourseId = firstPlayerTee?.course_id ? String(firstPlayerTee.course_id) : ''
      setCourseId(observedCourseId || String(fallbackCourse.id))
    }

    // The golfers' own tee says which holes the event plays, so re-entry keeps Front 9.
    if (!setupConfig?.walkonHoleType) {
      const observedHoleType = holeTypeFromTee(firstPlayerTee)
      if (observedHoleType) { setHoleType(observedHoleType) }
    }

    // Walk-on has no per-golfer tees at all -- the event's default tees are saved from Continue.
    if (isWalkon) { return }

    // The leader's own pick, once this step has been through, wins over the resolved default.
    const teeToSave = setupConfig?.teeId || defaultTeeId
    if (isIndividual) {
      preAssignAllPlayerTees(teeSheetFoursomes, teeToSave)
    } else {
      assignDefaultTeeToPlayers({
        men: sheetTees.men?.id || teeToSave,
        women: sheetTees.women?.id || sheetTees.men?.id || teeToSave,
      }, teeSheetFoursomes)
    }
    // Runs once courses and the tee sheet have arrived; the ref keeps it to one pass.
  }, [ courses, teeSheetLoading, setupConfig ])

  const selectedCourse = courses.find((c) => String(c.id) === String(courseId)) || null
  const tees = selectedCourse?.attributes?.tees || []
  const courseTees = tees.map((tee) => ({ id: String(tee.id), name: tee.name }))
  // FieldSelect shows a placeholder only while the value is empty.
  const coursePlaceholder = courses.length === 0 ? t('no_courses') : t('select_course')
  const teePlaceholder = (courseId && tees.length === 0) ? t('no_tees') : t('select_tee')

  // Walk-on default tees: the leader's pick, else the course's default.
  const effTeeM = teeM || defaultTeeFor(selectedCourse, 'M')
  const effTeeF = teeF || defaultTeeFor(selectedCourse, 'F')
  // A nine-hole tee still shows the select, with its one value.
  const nineHoleTee = !!tees.find((tee) => String(tee.id) === String(effTeeM))?.nine_hole_course
  const holeTypeOptions = (nineHoleTee ? [ NINE_HOLES ] : [ '1', '2', '3' ])
    .map((id) => ({ id, name: t(HOLES_PLAYED_KEYS[id]) }))
  const effHoleType = nineHoleTee ? NINE_HOLES : holeType

  const foursomes = teeSheetData?.data || []
  // From the round, falling back to the widest occupied group while it loads.
  const seatCount = parseInt(serverRound?.attributes?.pairing_group_size, 10)
    || Math.max(4, ...foursomes.map((g) => (g.attributes?.players || []).length))
  const unplaced = unplacedData?.players || []
  const placedPlayers = foursomes.flatMap((f) => f.attributes?.players || [])
  const allPlayers = [ ...placedPlayers, ...unplaced ]
  const placedPlayerIds = placedPlayers.map((p) => p.player_round_id).filter(Boolean)

  // A new course's tee ids differ, so the picks re-default and the golfers move with them.
  const handleCourseChange = async (id) => {
    setCourseId(id)
    setTeeM('')
    setTeeF('')
    const course = courses.find((one) => String(one.id) === String(id))
    await writeIfSame({ men: defaultTeeFor(course, 'M'), women: defaultTeeFor(course, 'F') })
  }

  // An explicit tee goes to everyone, a course default splits by gender; `holes_type` resolves front/back 9.
  const writeEventTee = async (players, { men = effTeeM, women = effTeeF, holes = holeType } = {}) => {
    // Placed only: set_course_and_tee rejects a call whose ids are all unplaced.
    const placed = new Set(placedPlayerIds.map(String))
    const golfers = players.filter((p) => p.player_round_id && placed.has(String(p.player_round_id)))
    if (!roundId || !golfers.length) { return true }

    const menTee = men || women
    const womenTee = women || men
    if (!menTee) { return true }

    const { groups: teeGroups } = teeGroupsFor(golfers, { menTee, womenTee })

    // Silent, one invalidation at the end, under the TMS API's 20 GETs a minute.
    try {
      for (const [ tee, playerIds ] of teeGroups) {
        if (!tee || !playerIds.length) { continue }
        // Per tee, not per card: the genders can sit on tees of different lengths.
        const isNineHole = !!tees.find((courseTee) => String(courseTee.id) === String(tee))?.nine_hole_course
        // eslint-disable-next-line no-await-in-loop
        const result = await setCourseAndTee({
          leagueId,
          roundId,
          teeId: tee,
          playerIds,
          holesType: holesTypeFor(isNineHole ? NINE_HOLES : holes),
          silent: true,
        })
        if (result.error) { notifyFailure(t('errors.tee_assignment_failed')); return false }
      }
      return true
    } finally {
      dispatch(glApi.util.invalidateTags([ 'TeeSheet' ]))
    }
  }

  // "Same tee for all" writes each pick at once, passed in as setState has not landed; the others wait for Apply.
  const writeIfSame = async (picks) => {
    if (isWalkon || mode !== 'same') { return }
    await writeEventTee(allPlayers, picks)
  }

  const handleMensTeeChange = async (newTee) => {
    setTeeM(newTee)
    await writeIfSame({ men: newTee })
  }

  const handleWomensTeeChange = async (newTee) => {
    setTeeF(newTee)
    await writeIfSame({ women: newTee })
  }

  // Puts every placed golfer back on the card's tees.
  const handleApplyDefaultToAll = async () => {
    if (!roundId) { return }
    setApplyingToAll(true)
    await writeEventTee(placedPlayers)
    setApplyingToAll(false)
  }

  // Re-writes the tees when the holes change.
  const handleHoleTypeChange = async (newHoleType) => {
    setHoleType(newHoleType)
    await writeIfSame({ holes: newHoleType })
  }

  const handlePlayerTeeChange = async (playerRoundId, newTeeId) => {
    if (!newTeeId || !roundId || !playerRoundId) { return }
    setLoadingTeePlayerIds((prev) => [ ...prev, playerRoundId ])
    const result = await setCourseAndTee({
      leagueId, roundId, teeId: newTeeId, playerIds: [ playerRoundId ],
      holesType: holesTypeFor(effHoleType), silent: true,
    })
    setLoadingTeePlayerIds((prev) => prev.filter((id) => id !== playerRoundId))
    if (result.error) { showErrorNotification(t('errors.update_tee_failed')); return }
    // Patch the one row rather than refetch, under the TMS API throttle.
    // The saved tee, as the sheet holds it: Review and Pairings write tees back from this cache.
    const saved = result.data?.tee
    const picked = {
      ...tees.find((tee) => String(tee.id) === String(newTeeId)), ...saved, parent_id: saved?.virtual ? String(newTeeId) : '',
    }
    dispatch(glApi.util.updateQueryData('getTeeSheet', { leagueId, roundId }, (draft) => {
      draft.data?.forEach((foursome) => {
        (foursome.attributes?.players || []).forEach((player) => {
          if (String(player.player_round_id) === String(playerRoundId)) { player.tee = picked }
        })
      })
    }))
  }

  // Deals the field into single-seat groups (helpers/walkonPlacement, which Review reuses).
  const placeWalkonGolfers = async () => {
    setPlacingWalkon(true)
    try {
      return await placeWalkonField({
        round: { leagueId, roundId },
        field: { unplaced, foursomes },
        // Whichever tee was set stands in for the other: one tee beats a group score entry refuses.
        tees: {
          menTee: effTeeM || effTeeF,
          womenTee: effTeeF || effTeeM,
          holesType: holesTypeFor(effHoleType),
          // effHoleType, not the raw pick, so a nine-hole course never starts on hole 10.
          hole: walkonStartingHole(effHoleType),
        },
        api: { createRow, setCourseAndTee, setHole, refetchTeeSheet },
        onError: () => showErrorNotification(t('errors.tee_assignment_failed')),
      })
    } finally {
      // Silent writes, refreshed on failure too: a halfway run still placed golfers.
      dispatch(glApi.util.invalidateTags([ 'TeeSheet' ]))
      setPlacingWalkon(false)
    }
  }

  const handleContinue = async () => {
    const saveConfig = (picks) => dispatch(setSetupConfig({
      ...(setupConfig || {}), courseId, courseName: selectedCourse?.attributes?.name || '', ...picks,
    }))
    const eventTees = { walkonTeeM: effTeeM, walkonTeeF: effTeeF, walkonHoleType: effHoleType }
    if (isWalkon) {
      saveConfig(eventTees)
      if (roundId && courseId) {
        // Ids are 19-digit strings (beyond JS safe-integer range), so send them as strings.
        const result = await updateRoundExpressEventSettings({
          leagueId,
          roundId,
          eventWithNoTeesheet: {
            default_course: courseId,
            ...(effTeeM ? { default_tee_m: effTeeM } : {}),
            ...(effTeeF ? { default_tee_f: effTeeF } : {}),
            hole_type: walkonHoleTypeForSave(effHoleType),
          },
        })
        if (result.error) { notifyFailure(t('errors.tee_assignment_failed')); return }
      }
      // Only once the defaults above are stored, since the placement reads the tees from them.
      if (!(await placeWalkonGolfers())) { return }
      dispatch(markStepCompleted('course_tee'))
      dispatch(goToNextStep())
      return
    }
    if (mode === 'same') {
      if (!effTeeM && !effTeeF) { return }
      const menTee = tees.find((tee) => String(tee.id) === String(effTeeM))
      const womenTee = tees.find((tee) => String(tee.id) === String(effTeeF))
      saveConfig({
        teeId: effTeeM,
        ...eventTees,
        // Both names when the genders play different tees, so Review can say so in one line.
        teeName: menTee?.name === womenTee?.name
          ? (menTee?.name || '')
          : [ menTee?.name, womenTee?.name ].filter(Boolean).join(' / '),
      })

      if (!(await writeEventTee(allPlayers))) { return }
    } else {
      saveConfig({ teeName: t('individual_tees') })
    }

    dispatch(markStepCompleted('course_tee'))
    dispatch(goToNextStep())
  }

  if (coursesLoading) { return <LoadingState label={t('loading')}/> }

  return (
    <div className="gl-event-setup-wrap">
      <LiveMessage message={liveMessage}/>
      <PageHeader
        title={t('title')}
        subtitle={isWalkon ? t('walkon.subtitle') : t('subtitle')}
      />

      <div className="gl-event-setup-card">
        <div className="gl-event-setup-card-hd">
          <span className="gl-event-setup-card-title">{t('card_title')}</span>
          {/* The mode switches the whole card, so it sits in the header; same control as Games. */}
          {!isWalkon && (
            <div className="gl-event-setup-seg-control" role="group" aria-label={t('mode_label')}>
              {[ 'same', 'individual' ].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`gl-event-setup-seg-btn${mode === value ? ' is-active' : ''}`}
                  onClick={() => setMode(value)}
                  aria-pressed={mode === value}
                >
                  {t(value === 'same' ? 'mode_same' : 'individual_tees')}
                </button>
              ))}
            </div>
          )}
        </div>
        {isWalkon && (
          <div className="gl-event-setup-card-note" role="note">{t('walkon.note')}</div>
        )}
        <div className="gl-event-setup-card-body">
          <div className="gl-event-setup-ct-field-row">
            <FieldSelect
              id="ct-course"
              label={t('course_label')}
              options={courses.map((c) => ({ id: c.id, name: c.attributes.name }))}
              value={courseId}
              onChange={handleCourseChange}
              disabled={courses.length === 0}
              placeholder={coursePlaceholder}
            />

            {/* The event tee in both modes: same mode writes it at once, individual measures exceptions against it. */}
            <FieldSelect
              id="ct-holes"
              label={t('holes_label')}
              options={holeTypeOptions}
              value={effHoleType}
              onChange={handleHoleTypeChange}
              disabled={saving || nineHoleTee}
            />

            <FieldSelect
              id="ct-tee-m"
              label={t('mens_tee')}
              options={courseTees}
              value={effTeeM}
              onChange={handleMensTeeChange}
              disabled={tees.length === 0 || saving}
              placeholder={teePlaceholder}
            />

            <FieldSelect
              id="ct-tee-f"
              label={t('womens_tee')}
              options={courseTees}
              value={effTeeF}
              onChange={handleWomensTeeChange}
              disabled={tees.length === 0 || saving}
              placeholder={teePlaceholder}
            />

            {!isWalkon && mode === 'individual' && (
              <div className="gl-event-setup-ct-apply-cell">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleApplyDefaultToAll}
                  disabled={applyingToAll || preAssigning || (!effTeeM && !effTeeF) || placedPlayerIds.length === 0}
                >
                  {applyingToAll ? t('applying') : t('apply_to_all')}
                </Button>
              </div>
            )}

          </div>
        </div>
      </div>

      {!isWalkon && mode === 'individual' && (
        <IndividualTeeSection
          foursomes={foursomes}
          seatCount={seatCount}
          tees={courseTees}
          defaultTeeId={effTeeM ? String(effTeeM) : ''}
          defaultWomensTeeId={effTeeF ? String(effTeeF) : ''}
          onPlayerTeeChange={handlePlayerTeeChange}
          loadingTeePlayerIds={loadingTeePlayerIds}
          preAssigning={preAssigning}
        />
      )}

      <WizardFooter
        onBack={() => dispatch(goToPreviousStep())}
        onPrimary={handleContinue}
        primaryLabel={(saving || savingWalkon || placingWalkon) ? t('saving') : t('continue')}
        primaryDisabled={isWalkon
          ? (!courseId || savingWalkon || placingWalkon)
          : (mode === 'same' ? ((!effTeeM && !effTeeF) || saving) : (saving || preAssigning))}
      />
    </div>
  )
}

export default CourseTeeStep
